import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import * as authService from '../services/authService';
import './ProfilePage.css';

const ProfilePage = () => {
  const { user, tokens, updateUser } = useAuth();
  const { t } = useLanguage();

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    full_name: '',
    age: '',
    weight: '',
    height: '',
    gender: '',
    activity_level: '',
    goal: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        age: user.age || '',
        weight: user.weight || '',
        height: user.height || '',
        gender: user.gender || '',
        activity_level: user.activity_level || '',
        goal: user.goal || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const updatedUser = await authService.updateUserProfile(tokens.access_token, formData);
      updateUser(updatedUser);
      setMessage(t('profile_updated_success'));
      setEditing(false);

      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (err) {
      setError(err.message || t('profile_update_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: user.full_name || '',
      age: user.age || '',
      weight: user.weight || '',
      height: user.height || '',
      gender: user.gender || '',
      activity_level: user.activity_level || '',
      goal: user.goal || '',
    });
    setEditing(false);
    setError('');
    setMessage('');
  };

  const calculateBMI = () => {
    if (formData.weight && formData.height) {
      const heightInMeters = formData.height / 100;
      const bmi = formData.weight / (heightInMeters * heightInMeters);
      return bmi.toFixed(1);
    }
    return null;
  };

  const bmi = calculateBMI();

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1>{t('profile')}</h1>
        <p className="profile-subtitle">{t('profile_subtitle')}</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          {message}
        </div>
      )}

      <div className="profile-container">
        {/* User Info Card */}
        <div className="profile-card">
          <div className="profile-avatar">
            <div className="avatar-circle">
              {user?.full_name?.charAt(0)?.toUpperCase() || '👤'}
            </div>
          </div>

          <div className="profile-info">
            <h2>{user?.full_name || t('user')}</h2>
            <p className="profile-telegram">
              Telegram ID: <strong>{user?.telegram_id}</strong>
            </p>
            <p className="profile-joined">
              {t('joined')}: {new Date(user?.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Profile Form */}
        <div className="profile-form-card">
          <div className="form-header">
            <h3>{t('personal_information')}</h3>
            {!editing && (
              <button className="btn-edit" onClick={() => setEditing(true)}>
                ✏️ {t('edit')}
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('full_name')}</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>{t('age')}</label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-input"
                  min="1"
                  max="120"
                />
              </div>

              <div className="form-group">
                <label>{t('gender')}</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-input"
                >
                  <option value="">{t('select_gender')}</option>
                  <option value="male">{t('male')}</option>
                  <option value="female">{t('female')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('weight')} (kg)</label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-input"
                  min="20"
                  max="300"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>{t('height')} (cm)</label>
                <input
                  type="number"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-input"
                  min="100"
                  max="250"
                />
              </div>

              <div className="form-group">
                <label>{t('activity_level')}</label>
                <select
                  name="activity_level"
                  value={formData.activity_level}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-input"
                >
                  <option value="">{t('select_activity_level')}</option>
                  <option value="sedentary">{t('sedentary')}</option>
                  <option value="lightly_active">{t('lightly_active')}</option>
                  <option value="moderately_active">{t('moderately_active')}</option>
                  <option value="very_active">{t('very_active')}</option>
                  <option value="extra_active">{t('extra_active')}</option>
                </select>
              </div>

              <div className="form-group">
                <label>{t('goal')}</label>
                <select
                  name="goal"
                  value={formData.goal}
                  onChange={handleChange}
                  disabled={!editing}
                  className="form-input"
                >
                  <option value="">{t('select_goal')}</option>
                  <option value="lose_weight">{t('lose_weight')}</option>
                  <option value="maintain_weight">{t('maintain_weight')}</option>
                  <option value="gain_weight">{t('gain_weight')}</option>
                  <option value="build_muscle">{t('build_muscle')}</option>
                </select>
              </div>

              {bmi && (
                <div className="form-group bmi-display">
                  <label>{t('bmi')}</label>
                  <div className="bmi-value">
                    <span className="bmi-number">{bmi}</span>
                    <span className="bmi-label">
                      {bmi < 18.5 && t('underweight')}
                      {bmi >= 18.5 && bmi < 25 && t('normal_weight')}
                      {bmi >= 25 && bmi < 30 && t('overweight')}
                      {bmi >= 30 && t('obese')}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {editing && (
              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  {t('cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? t('saving') : t('save_changes')}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
